// apps/web/src/pages/author/sections/Constraints.tsx
//
// Phase 9 Plan 10 Task 2 — Constraints form section (authr-01 / authr-02 /
// authr-03). REPLACES the Phase 8 stub that only edited the legacy
// summary-checklist visibleWhen shape; Phase 9 generalizes the section to
// the full 5-kind Constraint editor (D-15).
//
// Phase 9 Plan 13 update — drag-handle reorder via @dnd-kit:
//   The Plan 09-10 implementation used FieldArrayRow's up/down arrows for
//   reorder. Plan 09-13 wraps each row in <SortableList>/<SortableItem>
//   (built on Plan 09-11's @dnd-kit primitives) so reorder is a drag on the
//   per-row drag-handle. FieldArrayRow chrome (remove button + body) is
//   preserved; the up/down arrow buttons become inert (canMoveUp = canMoveDown
//   = false, no-op handlers) so they collapse to disabled state — the chrome
//   container is shared with Phase 8 sections that still use arrow reorder.
//   The drag-handle aria-label is "Reorder constraint {index + 1}" per
//   UI-SPEC line 457.
//
// Composition:
//   - Top-level useFieldArray on `constraints` (parent form path — RHF
//     top-level just like Phase 8). Per-row useWatch ONLY (per-path) — no
//     top-level useWatch.
//   - CycleErrorBanner above the Fieldset (subscribes to the debounced
//     useConstraintEvaluation hook).
//   - Per-row: ConflictBadge (when row id is in winningIds OR supersededIds)
//     + kind Select (5 options) + ConstraintFormDispatcher (kind-routed
//     sub-form) + FieldArrayRow chrome (remove only — drag-handle owns reorder).
//   - Add Constraint Dialog with kind picker → calls the matching
//     defaultXConfig() factory → append() to useFieldArray.
//
// Evaluator inputs:
//   - constraints: per-path useWatch on "constraints" (per-path narrow
//     subscription; the OptionCategories.tsx Phase 8 section uses the same
//     pattern for "widgets").
//   - context: a synthetic EvalContext built from the per-path useWatch on
//     "widgets". The author UI has no spread context, so batch-set-count /
//     current-batch-set-index / active-mode-id / etc are reasonable
//     defaults (1 / 0 / undefined / etc). The evaluator surfaces cycle +
//     conflict structural problems regardless of context values; the
//     context-dependent verdicts (visibility, dependency runtime status)
//     are advisory at edit time and authoritative at SpreadComposer render
//     time (Plan 09-11/12).
//
// authr-06 hygiene: per-path useWatch ONLY — narrow subscriptions to
// specific paths ("constraints", "widgets", `constraints.${idx}.kind`).
// NO top-level useWatch (Pitfall 1 + 2 hygiene). Plan 09-13's grep gate
// extends to these files.

import {
  type Constraint,
  type EvalContext,
} from "@the-recipe-spread/shared";
import { memo, useCallback, useId, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { SortableItem } from "@/components/layout/SortableItem";
import { SortableList } from "@/components/layout/SortableList";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConstraintEvaluation } from "@/lib/constraints/useConstraintEvaluation.js";
import {
  ConstraintFormDispatcher,
  type ConstraintKind,
} from "../constraint-config/dispatcher.js";
import { ConflictBadge } from "../constraint-config/ConflictBadge.js";
import {
  defaultCardinalityConfig,
} from "../constraint-config/cardinality-config.js";
import {
  defaultDependencyConfig,
} from "../constraint-config/dependency-config.js";
import {
  defaultPropagationConfig,
} from "../constraint-config/propagation-config.js";
import {
  defaultSelectabilityConfig,
} from "../constraint-config/selectability-config.js";
import {
  defaultVisibilityConfig,
} from "../constraint-config/visibility-config.js";
import { FieldArrayRow } from "../FieldArrayRow";
import { CycleErrorBanner } from "./CycleErrorBanner";

// =============================================================================
// CONSTRAINT_KINDS — closed list (5 entries, generalized from Phase 8 stub).
// =============================================================================
//
// Surfaced in the kind Select per row + in the Add-constraint Dialog kind
// picker. Phase 8 disabled all but "visibility"; Phase 9 enables all 5.
// Title-case in the user-facing form per UI-SPEC §"Constraint row — kind
// selector options" (lines ~462); schema value stays lowercase via Select
// `value` binding.

const CONSTRAINT_KINDS = [
  "cardinality",
  "propagation",
  "visibility",
  "dependency",
  "selectability",
] as const satisfies ReadonlyArray<ConstraintKind>;

const KIND_LABELS: Readonly<Record<ConstraintKind, string>> = Object.freeze({
  cardinality: "Cardinality",
  propagation: "Propagation",
  visibility: "Visibility",
  dependency: "Dependency",
  selectability: "Selectability",
});

// =============================================================================
// DEFAULT_FACTORIES — single source of truth lives in each per-kind sub-form.
// =============================================================================
//
// W11 — closed-registry discipline mirrors WidgetInstances.tsx Phase 8 pattern
// (apps/web/src/pages/author/sections/WidgetInstances.tsx line 81-90).
// Object.freeze + ReadonlyMap; Plan 09-13 grep gate enforces.

const _factories = new Map<ConstraintKind, () => unknown>([
  ["cardinality", defaultCardinalityConfig],
  ["propagation", defaultPropagationConfig],
  ["visibility", defaultVisibilityConfig],
  ["dependency", defaultDependencyConfig],
  ["selectability", defaultSelectabilityConfig],
]);

const DEFAULT_FACTORIES: ReadonlyMap<ConstraintKind, () => unknown> =
  Object.freeze(_factories);

// Plan 09-13: drag-handle reorder owns repositioning; the FieldArrayRow chrome
// up/down arrows are inert (canMoveUp=canMoveDown=false). NOOP_REORDER is a
// stable module-level reference to avoid recreating a function on every render.
const NOOP_REORDER = (): void => {};

// =============================================================================
// buildSyntheticContext — build an EvalContext from form-state widgets.
// =============================================================================
//
// The author UI has no spread-level state (no batch sets, no active mode);
// only the constraint sub-system needs structural cycle + conflict feedback.
// Author-time visibility/dependency verdicts are advisory — authoritative
// evaluation happens at SpreadComposer render time (Plan 09-11/12).

function buildSyntheticContext(
  widgets: ReadonlyArray<{ instanceId?: string; state?: unknown }> | undefined,
): EvalContext {
  const widgetStates = new Map<string, Readonly<Record<string, unknown>>>();
  if (Array.isArray(widgets)) {
    for (const w of widgets) {
      if (typeof w?.instanceId === "string" && w.instanceId.length > 0) {
        const s =
          w.state !== null && typeof w.state === "object"
            ? (w.state as Record<string, unknown>)
            : {};
        widgetStates.set(w.instanceId, s);
      }
    }
  }
  return {
    widgetStates,
    batchSetCount: 1,
    currentBatchSetIndex: 0,
    currentRecipeId: "",
    otherBatchSetsAllEmpty: true,
    activeModeId: undefined,
    spreadRecipeCount: 1,
  };
}

// =============================================================================
// Constraints — the section component (REPLACES Phase 8 stub).
// =============================================================================

export function Constraints(): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "constraints",
  });

  // Per-path useWatch on the constraints array (so reorders + per-row edits
  // surface to the evaluator). The hook itself adds the 100ms debounce so
  // every keystroke doesn't re-fire evaluate().
  const watchedConstraints = useWatch({ control, name: "constraints" }) as
    | ReadonlyArray<Constraint>
    | undefined;
  const constraints: ReadonlyArray<Constraint> = useMemo(
    () => (Array.isArray(watchedConstraints) ? watchedConstraints : []),
    [watchedConstraints],
  );

  // Per-path useWatch on widgets — feeds the synthetic EvalContext.
  const watchedWidgets = useWatch({ control, name: "widgets" }) as
    | ReadonlyArray<{ instanceId?: string; state?: unknown }>
    | undefined;
  const evalContext: EvalContext = useMemo(
    () => buildSyntheticContext(watchedWidgets),
    [watchedWidgets],
  );

  const evalResult = useConstraintEvaluation(constraints, evalContext);

  // Refs for the per-row Fieldset, so cycle-banner / superseded-badge links
  // can scrollIntoView + focus the matching row.
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Plan 09-16: useCallback-stabilized handlers so the React.memo'd
  // ConstraintRow (defined below) sees stable reference props across
  // re-renders of unaffected rows. handleLocate reads rowRefs.current (stable
  // ref object), so deps is []. onRemoveRow / registerRowRefStable take the
  // index as a parameter so a SINGLE stable function reference is shared by
  // every row.
  const handleLocate = useCallback((index: number): void => {
    const el = rowRefs.current[index];
    if (el !== null && el !== undefined) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
    }
  }, []);
  const onRemoveRow = useCallback(
    (i: number): void => {
      remove(i);
    },
    [remove],
  );
  const registerRowRefStable = useCallback(
    (i: number, el: HTMLDivElement | null): void => {
      rowRefs.current[i] = el;
    },
    [],
  );

  // Plan 09-16 (B2): lift countSupersededBelow into the parent. Computing the
  // per-winner-row superseded count ONCE per render produces a primitive
  // (number) per row prop that correctly invalidates the React.memo'd
  // ConstraintRow when a sibling row's superseded status changes. A naive
  // Set-membership equality on winningIds would miss this — the winner's own
  // membership in winningIds doesn't change when only a SIBLING's superseded
  // status flips, so the memo would skip the re-render and leave the badge
  // count stale. By passing each row its own primitive `supersededCount: number`
  // prop, the memo equality stays primitive-only AND correct.
  const supersededCountByWinningIndex = useMemo((): ReadonlyMap<number, number> => {
    const byWinner = new Map<number, number>();
    for (let winnerIdx = 0; winnerIdx < constraints.length; winnerIdx++) {
      const winner = constraints[winnerIdx];
      if (winner === undefined) continue;
      const key = conflictKeyOf(winner);
      if (key === null) continue;
      let count = 0;
      for (let i = winnerIdx + 1; i < constraints.length; i++) {
        const c = constraints[i];
        if (c === undefined) continue;
        if (
          conflictKeyOf(c) === key &&
          evalResult.supersededConstraintIds.has(`constraint-${i}`)
        ) {
          count++;
        }
      }
      if (count > 0) byWinner.set(winnerIdx, count);
    }
    return byWinner;
  }, [constraints, evalResult.supersededConstraintIds]);

  // ---- Add-constraint Dialog state -------------------------------------------
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pickedKind, setPickedKind] = useState<ConstraintKind | undefined>(undefined);
  const handleAddConstraint = (): void => {
    if (pickedKind === undefined) return;
    const factory = DEFAULT_FACTORIES.get(pickedKind);
    const seed = factory ? factory() : { kind: pickedKind };
    append(seed);
    setAddDialogOpen(false);
    setPickedKind(undefined);
  };

  return (
    <Fieldset legend="CONSTRAINTS">
      <CycleErrorBanner cycles={evalResult.cycles} onLocateConstraint={handleLocate} />

      {fields.length === 0 ? (
        <EmptyState
          title="No constraints yet."
          cta={{
            label: "Add constraint",
            onClick: () => setAddDialogOpen(true),
          }}
        />
      ) : (
        <>
          {/* Plan 09-13 drag-handle reorder: SortableList wraps the field-array
              iteration; each row sits inside a <SortableItem> whose
              dragHandleAriaLabel is "Reorder constraint {index + 1}" per
              UI-SPEC line 457. Up/down arrows on FieldArrayRow are inert
              (canMoveUp=canMoveDown=false + no-op handlers) — the drag-handle
              is now the sole reorder affordance, but the FieldArrayRow remove
              button is preserved. */}
          <SortableList
            items={fields.map((f) => ({ id: f.id }))}
            onReorder={(oldIdx, newIdx) => move(oldIdx, newIdx)}
          >
            <div className="flex flex-col gap-2">
              {fields.map((field, index) => (
                <SortableItem
                  key={field.id}
                  id={field.id}
                  dragHandleAriaLabel={`Reorder constraint ${index + 1}`}
                >
                  <ConstraintRow
                    index={index}
                    onRemove={onRemoveRow}
                    isWinning={evalResult.winningConstraintIds.has(
                      `constraint-${index}`,
                    )}
                    isSuperseded={evalResult.supersededConstraintIds.has(
                      `constraint-${index}`,
                    )}
                    supersededCount={
                      supersededCountByWinningIndex.get(index) ?? 0
                    }
                    constraintsSnapshot={constraints}
                    onLocate={handleLocate}
                    registerRowRef={registerRowRefStable}
                  />
                </SortableItem>
              ))}
            </div>
          </SortableList>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            className="self-start"
          >
            Add constraint
          </Button>
        </>
      )}

      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          setAddDialogOpen(open);
          if (!open) setPickedKind(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add constraint</DialogTitle>
            <DialogDescription>
              Pick a constraint kind. Configuration fields appear below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="constraint-add-kind-select">
              Kind
            </label>
            <Select
              value={pickedKind ?? ""}
              onValueChange={(v) => setPickedKind(v as ConstraintKind)}
            >
              <SelectTrigger id="constraint-add-kind-select" data-testid="constraint-add-kind">
                <SelectValue placeholder="Choose a kind…" />
              </SelectTrigger>
              <SelectContent>
                {CONSTRAINT_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleAddConstraint}
              disabled={pickedKind === undefined}
              data-testid="constraint-add-confirm"
            >
              Add constraint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fieldset>
  );
}
Constraints.displayName = "Constraints";

// =============================================================================
// ConstraintRow — per-row chrome + dispatcher + ConflictBadge.
// =============================================================================

// Plan 09-16: ConstraintRowProps now carries PRIMITIVES instead of Sets.
// React.memo equality (areConstraintRowPropsEqual below) compares isWinning /
// isSuperseded / supersededCount as primitive `===` reads. Set-membership
// computation lives in the parent (Constraints) so each row sees only
// primitives — this prevents the per-row re-render storm under @dnd-kit
// useSortable + form-state churn (AUTHR-06 closure).
interface ConstraintRowProps {
  index: number;
  onRemove: (index: number) => void;
  isWinning: boolean;
  isSuperseded: boolean;
  supersededCount: number;
  constraintsSnapshot: ReadonlyArray<Constraint>;
  onLocate: (index: number) => void;
  registerRowRef: (index: number, el: HTMLDivElement | null) => void;
}

function ConstraintRowImpl({
  index,
  onRemove,
  isWinning,
  isSuperseded,
  supersededCount,
  constraintsSnapshot,
  onLocate,
  registerRowRef,
}: ConstraintRowProps): React.ReactElement {
  const { control } = useFormContext();

  // Per-path useWatch — narrow subscription to the row's kind only.
  const kindFromForm = useWatch({ control, name: `constraints.${index}.kind` });
  const kind = typeof kindFromForm === "string" ? kindFromForm : "visibility";
  const constraintId = `constraint-${index}`;
  const kindSelectId = useId();

  // Resolve the winning constraint index + kind for the SUPERSEDED badge
  // tooltip. We replicate the same first-wins key derivation the evaluator
  // uses (conflictKeyOf) at the row level so we can find the winning row's
  // document position without threading a separate map. constraintsSnapshot
  // is reference-compared by the memo equality below — when it stays the same,
  // the memo skips this work entirely along with the rest of the row body.
  let winningIndex: number | undefined;
  let winningKind: string | undefined;
  if (isSuperseded) {
    const winner = findWinningIndex(constraintsSnapshot, index);
    if (winner !== null) {
      winningIndex = winner;
      const winningConstraint = constraintsSnapshot[winner];
      if (winningConstraint !== undefined && typeof winningConstraint.kind === "string") {
        winningKind = winningConstraint.kind;
      }
    }
  }

  return (
    <div
      ref={(el) => registerRowRef(index, el)}
      tabIndex={-1}
      data-constraint-id={constraintId}
      className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md"
    >
      <FieldArrayRow
        singular="constraint"
        index={index}
        // Plan 09-13 drag-handle reorder: up/down arrows on the chrome are
        // inert. canMoveUp=canMoveDown=false collapses them to disabled state
        // (FieldArrayRow renders disabled buttons via opacity-40 +
        // cursor-not-allowed). The SortableItem drag-handle wrapping this row
        // owns reorder; the remove button below is preserved.
        canMoveUp={false}
        canMoveDown={false}
        onMoveUp={NOOP_REORDER}
        onMoveDown={NOOP_REORDER}
        onRemove={() => onRemove(index)}
        deleteDialogTitle="Delete constraint?"
        deleteDialogBody={`This ${kind} rule will be removed.`}
        deleteConfirmLabel="Delete constraint"
      >
        <div className="flex flex-col gap-3">
          {/* Conflict badge row (when applicable) */}
          {(isWinning || isSuperseded) && (
            <div className="flex items-center gap-2">
              {isWinning && (
                <ConflictBadge state="winning" supersededCount={supersededCount} />
              )}
              {isSuperseded && (
                <ConflictBadge
                  state="superseded"
                  {...(winningKind !== undefined ? { winningKind } : {})}
                  {...(winningIndex !== undefined ? { winningIndex } : {})}
                  onLocate={() => {
                    if (winningIndex !== undefined) onLocate(winningIndex);
                  }}
                />
              )}
            </div>
          )}

          <FormField
            control={control}
            name={`constraints.${index}.kind`}
            render={({ field }) => (
              <FormItem data-field-path={`constraints.${index}.kind`}>
                <FormLabel htmlFor={kindSelectId}>Kind</FormLabel>
                <Select
                  value={typeof field.value === "string" ? field.value : "visibility"}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger id={kindSelectId} data-testid={`constraint-${index}-kind`}>
                      <SelectValue placeholder="Pick a kind" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CONSTRAINT_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {KIND_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <ConstraintFormDispatcher
            constraintKind={kind}
            constraintId={constraintId}
            basePath={`constraints.${index}`}
          />
        </div>
      </FieldArrayRow>
    </div>
  );
}

// Plan 09-16: React.memo equality compares PRIMITIVES only (isWinning,
// isSuperseded, supersededCount, index) + reference equality on the stable
// parent callbacks and constraintsSnapshot. Mirrors Plan 09-08's PredicateNode
// memo pattern. AUTHR-06 budget closure (per-row re-render storm under @dnd-kit
// useSortable mount cost).
function areConstraintRowPropsEqual(
  prev: ConstraintRowProps,
  next: ConstraintRowProps,
): boolean {
  return (
    prev.index === next.index &&
    prev.isWinning === next.isWinning &&
    prev.isSuperseded === next.isSuperseded &&
    prev.supersededCount === next.supersededCount &&
    prev.onRemove === next.onRemove &&
    prev.onLocate === next.onLocate &&
    prev.registerRowRef === next.registerRowRef &&
    prev.constraintsSnapshot === next.constraintsSnapshot
  );
}

const ConstraintRow = memo(ConstraintRowImpl, areConstraintRowPropsEqual);

// =============================================================================
// Internal helpers — first-wins look-ups against the constraints snapshot.
// =============================================================================
//
// These mirror the evaluator's first-wins logic (packages/shared/src/
// constraints/evaluator.ts conflictKeyOf + the resolveConflicts inline scan)
// so the row can surface the winning constraint position WITHOUT
// re-threading another map through the props. Any future change to the
// conflict signature in evaluator.ts must be reflected here too — keep
// them lockstep.

function conflictKeyOf(c: Constraint): string | null {
  switch (c.kind) {
    case "visibility":
      return `visibility::${c.show.widgetInstanceId}::${c.show.pathInState}`;
    case "cardinality":
      return `cardinality::${c.resource}::${c.scope}`;
    case "dependency":
      return `dependency::${c.if.ref.widgetInstanceId}::${c.if.ref.pathInState}::${JSON.stringify(
        c.if.equals,
      )}`;
    case "propagation":
      return `propagation::${c.trigger.widgetInstanceId}::${c.trigger.pathInState}`;
    case "selectability":
      return null;
    default:
      return null;
  }
}

function findWinningIndex(
  constraints: ReadonlyArray<Constraint>,
  loserIndex: number,
): number | null {
  const loser = constraints[loserIndex];
  if (loser === undefined) return null;
  const key = conflictKeyOf(loser);
  if (key === null) return null;
  for (let i = 0; i < loserIndex; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    if (conflictKeyOf(c) === key) return i;
  }
  return null;
}

// Plan 09-16: countSupersededBelow REMOVED. The parent Constraints component
// now computes supersededCountByWinningIndex as a single useMemo'd
// Map<number, number> per render and threads each row its own primitive
// `supersededCount: number` prop. Keeping a dead per-row helper invites
// future drift between two definitions of the same logic.
